<script>
	// Color x size grid for one model: type quantities per cell, the small number is the stock in the chosen store.
	import { createEventDispatcher, onMount } from 'svelte';
	import fetchData from '../api.js';
	import UserStore from '../stores/user';
	import { setActiveCart } from '../stores/activeCart';
	import { money, imgUrl, bySize, swatch, MAIN_STORE } from '../lib/format';
	import { storeName } from '../lib/catalog';
	import Icon from './Icon.svelte';
	import ImageManager from './ImageManager.svelte';

	export let product;
	export let stores = {};
	export let cart = null;
	export let initialColor = ''; // the color shown on the product card when it was tapped

	const dispatch = createEventDispatcher();
	const variants = product.variants || [];

	const stockOf = (v, s) => Number(v.inventory && v.inventory[s]) || 0;
	const totalStock = (v) => Object.values(v.inventory || {}).reduce((sum, q) => sum + (Number(q) || 0), 0);
	// wholesale price from the server: price list 3 for VIBE, list 2 for ALDO MORO and ELIXIR
	const hasPrice = (v) => Number(v.wholesale_price) > 0;

	// what this model already has in the cart, per sku and store
	const cartItems = ((cart && cart.items) || []).filter((i) => String(i.parent_id) === String(product.product_id));
	const initial = {};
	for (const i of cartItems) (initial[i.sku] = initial[i.sku] || {})[String(i.store)] = Number(i.quantity) || 0;
	let qty = JSON.parse(JSON.stringify(initial));

	const storeIds = [...new Set([
		...variants.flatMap((v) => Object.keys(v.inventory || {}).filter((s) => stockOf(v, s) > 0)),
		...cartItems.map((i) => String(i.store))
	])].sort((a, b) => (a === MAIN_STORE ? -1 : b === MAIN_STORE ? 1 : Number(a) - Number(b)));
	let store = storeIds.includes(MAIN_STORE) ? MAIN_STORE : storeIds[0] || MAIN_STORE;
	const storeStock = (s) => variants.reduce((sum, v) => sum + stockOf(v, s), 0);

	const colors = [...new Set(variants.map((v) => v.color || ''))];
	const sizes = [...new Set(variants.map((v) => v.size || ''))].sort(bySize);
	const cell = {};
	for (const v of variants) cell[(v.color || '') + '|' + (v.size || '')] = v;
	const colorHasStock = (c) => variants.some((v) => (v.color || '') === c && totalStock(v) > 0);
	const colorInCart = (c) => cartItems.some((i) => (i.color || '') === c);
	let showAllColors = false;
	$: visibleColors = colors.filter((c) => showAllColors || colorHasStock(c) || colorInCart(c));
	$: hiddenColors = colors.length - colors.filter((c) => colorHasStock(c) || colorInCart(c)).length;

	const priced = variants.filter(hasPrice);
	const wholesale = priced.length ? Math.min(...priced.map((v) => Number(v.wholesale_price))) : 0;
	const consumer = Math.max(0, ...variants.map((v) => Number(v.regular_price) || 0));

	const imageOf = (c) => {
		const img = (product.colorImages || []).find((i) => i.color === c);
		return img ? imgUrl(img.image_url) : '';
	};
	let shownColor = (colors.includes(initialColor) && initialColor) || colors.find((c) => colorHasStock(c) && imageOf(c)) || colors.find((c) => imageOf(c)) || '';
	$: image = imageOf(shownColor) || imgUrl(product.colorImages && product.colorImages[0] && product.colorImages[0].image_url);

	const sum = (o) => Object.values(o || {}).reduce((s, n) => s + (Number(n) || 0), 0);
	$: units = variants.reduce((s, v) => s + sum(qty[v.sku]), 0);
	// wholesale price; the customer's discount is not shown (it is given at the warehouse)
	$: amount = variants.reduce((s, v) => s + sum(qty[v.sku]) * (Number(v.wholesale_price) || 0), 0);
	$: changed = variants.some((v) =>
		[...new Set([...Object.keys(initial[v.sku] || {}), ...Object.keys(qty[v.sku] || {})])]
			.some((s) => ((initial[v.sku] || {})[s] || 0) !== ((qty[v.sku] || {})[s] || 0)));

	// the template calls qtyIn(qty, store, v) so Svelte sees it depends on qty and store
	const qtyIn = (q, s, v) => (q[v.sku] || {})[s] || 0;
	const qtyOf = (v) => qtyIn(qty, store, v);

	function setQty(v, value) {
		const before = (initial[v.sku] || {})[store] || 0;
		let n = Math.max(0, Math.floor(Number(value) || 0));
		// never more than the stock, but keep what is already in the cart if stock dropped since
		n = Math.min(n, Math.max(stockOf(v, store), before));
		qty[v.sku] = { ...(qty[v.sku] || {}), [store]: n };
	}

	// tap a size = one more unit; hold it = type a quantity
	let held = false;
	let holdTimer;

	function pressStart(v) {
		held = false;
		clearTimeout(holdTimer);
		holdTimer = setTimeout(() => {
			held = true;
			const answer = prompt(`כמה יחידות? (במלאי ${stockOf(v, store)})`, String(qtyOf(v) || ''));
			if (answer !== null) setQty(v, answer);
		}, 600);
	}

	function pressEnd() {
		clearTimeout(holdTimer);
	}

	function tap(v) {
		if (held) {
			held = false;
			return;
		}
		setQty(v, qtyOf(v) + 1);
	}

	// "1 מכל מידה": one of every size of this color that has stock and is not picked yet
	function oneOfEach(c) {
		for (const s of sizes) {
			const v = cell[c + '|' + s];
			if (v && hasPrice(v) && stockOf(v, store) > 0 && !qtyOf(v)) setQty(v, 1);
		}
	}

	const colorIdOf = (c) => (variants.find((v) => (v.color || '') === c && v.color_id) || {}).color_id || '';

	let saving = false;
	let error = '';

	async function save() {
		saving = true;
		error = '';
		const token = $UserStore.data;
		let cartId = cart ? cart.id : null;
		try {
			for (const v of variants) {
				const touched = new Set([...Object.keys(initial[v.sku] || {}), ...Object.keys(qty[v.sku] || {})]);
				for (const s of touched) {
					const before = (initial[v.sku] || {})[s] || 0;
					const after = (qty[v.sku] || {})[s] || 0;
					if (before === after) continue;
					if (after === 0) {
						const item = cartItems.find((i) => i.sku === v.sku && String(i.store) === s);
						if (item) await fetchData({ action: 'agents_remove_cart_item', token, item_id: item.id });
						continue;
					}
					const payload = {
						action: 'agents_cart_add', token,
						sku: v.sku, quantity: after, store: s, name: v.name,
						price: v.wholesale_price, price_before_discount: v.regular_price,
						product_id: v.product_id, parent_id: v.parent_id || product.product_id, parent_sku: product.sku
					};
					if (cartId) payload.cart_id = cartId;
					const res = await fetchData(payload);
					if (!res || !res.data || !res.data.id) throw new Error((res && res.msg) || '');
					cartId = res.data.id;
				}
			}
			if (cartId) await setActiveCart(cartId);
			dispatch('saved');
		} catch (e) {
			error = e.message || 'לא הצלחנו לעדכן את הסל. בדוק את החיבור לאינטרנט ונסה שוב.';
			if (cartId) setActiveCart(cartId);
		}
		saving = false;
	}

	let managingImages = false;
	let zoom = false;

	function onKey(e) {
		if (e.key === 'Escape') dispatch('close');
	}
	onMount(() => {
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	});
</script>

<div class="backdrop" on:click={() => dispatch('close')} />
<aside class="sheet" aria-label="בחירת מידות">
	<div class="head">
		<button class="thumb" on:click={() => image && (zoom = true)} aria-label="הגדלת תמונה">
			{#if image}<img src={image} alt="" />{:else}<span class="ph">{product.vendor || ''}</span>{/if}
		</button>
		<div class="info">
			<span class="tag">{product.vendor || ''}{product.type ? ' · ' + product.type : ''}</span>
			<h2>{(product.name || '').trim()}</h2>
			<span class="muted">{product.sku}</span>
			<div class="price-box">
				{#if consumer}<span class="muted small">צרכן {money(consumer)}</span>{/if}
				{#if wholesale}
					<span class="small">סיטונאי</span><span class="big">{money(wholesale)}</span>
				{:else}
					<span class="badge-warn">אין מחיר סיטונאי לדגם הזה</span>
				{/if}
			</div>
		</div>
		<button class="icon-btn" on:click={() => dispatch('close')} aria-label="סגירה"><Icon name="close" /></button>
	</div>

	<div class="tools">
		<div class="stores">
			{#each storeIds as s}
				<button class="store" class:on={s === store} on:click={() => (store = s)}>
					{storeName(stores, s)} <span>{storeStock(s)}</span>
				</button>
			{/each}
		</div>
		<button class="link-btn" on:click={() => (managingImages = true)}><Icon name="image" size={18} /> תמונות</button>
	</div>
	<p class="hint">המספר בכל מידה = כמה יש במלאי · הקשה מוסיפה יחידה · לחיצה ארוכה = הקלדת כמות</p>

	<div class="scroll">
		<table class="matrix">
			<thead>
				<tr>
					<th class="ctitle">צבע</th>
					{#each sizes as s}<th>{s || '—'}</th>{/each}
				</tr>
			</thead>
			<tbody>
				{#each visibleColors as c}
					<tr>
						<td>
							<div class="color-cell">
								<button class="color" class:shown={c === shownColor} on:click={() => (shownColor = c)}>
									<span class="sw" style="background:{swatch(c)}" />
									<span class="cname">{c || 'ללא צבע'}{#if colorIdOf(c)}<small>צבע {colorIdOf(c)}</small>{/if}</span>
								</button>
								<button class="one-each" on:click={() => oneOfEach(c)}>1 מכל מידה</button>
							</div>
						</td>
						{#each sizes as s}
							{@const v = cell[c + '|' + s]}
							<td>
								{#if !v || (!hasPrice(v) && !qtyIn(qty, store, v))}
									<div class="q out"><span>–</span>{#if v}<small>אין מחיר</small>{/if}</div>
								{:else if stockOf(v, store) > 0 || qtyIn(qty, store, v) > 0}
									{@const n = qtyIn(qty, store, v)}
									<div class="q" class:has={n} class:over={n > stockOf(v, store)}>
										<button class="tap" on:click={() => tap(v)}
											on:pointerdown={() => pressStart(v)} on:pointerup={pressEnd} on:pointerleave={pressEnd} on:pointercancel={pressEnd}
											aria-label="הוספת יחידה במידה {s}">
											{#if n}<b>{n}</b><small>מלאי {stockOf(v, store)}</small>{:else}<span class="stock">{stockOf(v, store)}</span>{/if}
										</button>
										{#if n}<button class="minus" on:click={() => setQty(v, n - 1)} aria-label="הורדת יחידה">−</button>{/if}
									</div>
								{:else}
									<div class="q out"><span>–</span></div>
								{/if}
							</td>
						{/each}
					</tr>
				{/each}
			</tbody>
		</table>
		{#if hiddenColors > 0}
			<button class="link-btn" on:click={() => (showAllColors = !showAllColors)}>
				{showAllColors ? 'הסתרת צבעים בלי מלאי' : `${hiddenColors} צבעים בלי מלאי – להציג`}
			</button>
		{/if}
	</div>

	{#if error}<div class="alert-error err">{error}</div>{/if}
	<div class="foot">
		<div class="sum">נבחרו {units} יח׳ מהדגם<b>{money(amount)}</b></div>
		<div class="actions">
			<button class="btn-ghost" on:click={() => dispatch('close')}>ביטול</button>
			<button class="btn-primary" on:click={save} disabled={!changed || saving}>
				{saving ? 'מעדכן…' : cartItems.length ? 'עדכון הסל' : 'הוספה לסל'}
			</button>
		</div>
	</div>
</aside>

{#if zoom}
	<div class="zoom" on:click={() => (zoom = false)}>
		<img src={image} alt="" />
	</div>
{/if}

{#if managingImages}
	<ImageManager {product} {colors} on:close={() => (managingImages = false)} on:updated={(e) => { product = e.detail; dispatch('product', e.detail); }} />
{/if}

<style>
	.head { display: flex; gap: 18px; padding: 20px 24px; border-bottom: 1px solid var(--line); }
	.thumb { width: 120px; aspect-ratio: 3 / 4; border-radius: 10px; overflow: hidden; background: var(--photo); flex: none; border: 0; padding: 0; }
	.thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
	.ph { display: grid; place-items: center; height: 100%; color: var(--muted); font-weight: 700; font-size: 13px; }
	.info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
	.tag { font-size: 12px; letter-spacing: .06em; color: var(--muted); font-weight: 700; }
	h2 { margin: 0; font-size: 24px; line-height: 1.2; }
	.price-box { margin-top: auto; display: flex; flex-wrap: wrap; gap: 6px 14px; align-items: baseline; }
	.big { font-size: 26px; font-weight: 800; }
	.strike { color: var(--muted); text-decoration: line-through; }
	.small { font-size: 14px; }

	.tools { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 24px 4px; }
	.stores { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 4px; }
	.store {
		height: 46px; padding: 0 14px; border-radius: 12px; border: 1px solid var(--line); background: var(--surface);
		font-size: 15px; white-space: nowrap; display: flex; gap: 8px; align-items: center;
	}
	.store span { color: var(--muted); font-weight: 700; }
	.store.on { background: var(--ink); color: #fff; border-color: var(--ink); }
	.store.on span { color: rgba(255, 255, 255, .7); }
	.tools .link-btn { display: flex; align-items: center; gap: 6px; white-space: nowrap; }

	.scroll { flex: 1; overflow: auto; padding: 4px 24px 20px; }
	.matrix { width: 100%; border-collapse: separate; border-spacing: 6px 8px; }
	.matrix th { font-size: 14px; color: var(--muted); font-weight: 500; text-align: center; padding: 0; }
	.matrix th.ctitle { text-align: start; }
	.hint { margin: 2px 24px 0; color: var(--muted); font-size: 14px; }
	.color-cell { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; min-width: 130px; max-width: 180px; }
	.color {
		display: flex; align-items: center; gap: 10px;
		border: 0; background: none; padding: 4px 2px; font-weight: 700; font-size: 15px; text-align: start;
	}
	.color .sw { width: 26px; height: 26px; }
	.cname { display: flex; flex-direction: column; line-height: 1.15; }
	.cname small { font-weight: 500; color: var(--muted); font-size: 13px; }
	.color.shown .cname { text-decoration: underline; text-underline-offset: 4px; }
	.one-each { border: 0; background: none; color: var(--ink-2); font-size: 13px; text-decoration: underline; padding: 2px 2px 4px; min-height: 30px; }
	.q {
		position: relative; min-width: 62px; height: 64px; border: 1px solid var(--line-strong); border-radius: 12px; background: var(--surface);
	}
	.tap {
		width: 100%; height: 100%; border: 0; background: none; border-radius: 12px; padding: 0;
		display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px;
		-webkit-touch-callout: none; user-select: none;
	}
	.tap .stock { font-size: 20px; font-weight: 500; color: var(--ok); }
	.tap b { font-size: 24px; font-weight: 800; line-height: 1; }
	.tap small { font-size: 11px; color: var(--muted); white-space: nowrap; }
	.minus {
		position: absolute; top: -9px; left: -9px; width: 30px; height: 30px; border-radius: 50%;
		border: 1px solid var(--line-strong); background: var(--surface); font-size: 20px; line-height: 1; font-weight: 700;
		display: grid; place-items: center; box-shadow: var(--shadow);
	}
	.q.has { border-color: var(--ink); box-shadow: inset 0 0 0 1px var(--ink); background: #FAFAF8; }
	.q.over { border-color: var(--danger); box-shadow: inset 0 0 0 1px var(--danger); }
	.q.out { background: var(--bg); border-style: dashed; display: flex; flex-direction: column; align-items: center; justify-content: center; }
	.q.out span { color: #B9B6AF; font-size: 20px; font-weight: 700; line-height: 1; }
	.q.out small { font-size: 11px; color: var(--muted); }

	.err { margin: 0 24px 10px; }
	.foot {
		display: flex; align-items: center; justify-content: space-between; gap: 16px;
		padding: 16px 24px calc(16px + env(safe-area-inset-bottom)); border-top: 1px solid var(--line); background: var(--surface);
	}
	.sum { font-size: 15px; color: var(--muted); }
	.sum b { display: block; font-size: 22px; color: var(--ink); }
	.actions { display: flex; gap: 10px; }

	.zoom { position: fixed; inset: 0; z-index: 60; background: rgba(0, 0, 0, .85); display: grid; place-items: center; }
	.zoom img { max-width: 94vw; max-height: 94vh; object-fit: contain; border-radius: 8px; }
</style>
